import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import axiosInstance from "../axiosInstance";
import { isFullAdmin } from "../config/adminUsers";
import { getUserEmail } from "../utils/getUserEmail";

const ROLE_LABELS = {
  admin: "Tenant Admin",
  customersuccess: "Customer Success",
};

const AVAILABLE_ROLES = ["admin", "customersuccess"];

const getRoleLabel = (role) => ROLE_LABELS[role] || role;

const parseCustomClaims = (customClaims) => {
  if (!customClaims || typeof customClaims !== "object") return [];
  const entries = [];
  Object.entries(customClaims).forEach(([key, value]) => {
    if (key === "role") {
      entries.push({ role: value, tenantId: null });
    } else if (key.startsWith("tnt_")) {
      entries.push({ role: value, tenantId: key });
    }
  });
  return entries;
};

const UsersManagementTab = ({ tenants = [] }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState(null);
  const [userTenantMap, setUserTenantMap] = useState({});

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", displayName: "", role: "" });
  const [creatingUser, setCreatingUser] = useState(false);

  const [editDisplayNameDialog, setEditDisplayNameDialog] = useState(null);
  const [editDisplayNameValue, setEditDisplayNameValue] = useState("");
  const [savingDisplayName, setSavingDisplayName] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);

  const [rolesDialog, setRolesDialog] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loadingUserRoles, setLoadingUserRoles] = useState(false);
  const [assignRoleValue, setAssignRoleValue] = useState("");
  const [assignTenantValue, setAssignTenantValue] = useState([]);
  const [assigningRole, setAssigningRole] = useState(false);
  const [unassigningRole, setUnassigningRole] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!isFullAdmin(getUserEmail())) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get("/Users/all");
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setMessage({ type: "error", text: "Failed to load users." });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserTenants = useCallback((user) => {
    const claims = parseCustomClaims(user.customClaims);
    return claims
      .filter((c) => c.tenantId)
      .map((c) => {
        const t = tenants.find((tn) => tn.id?.toLowerCase() === c.tenantId?.toLowerCase());
        return { tenantId: c.tenantId, tenantName: t?.name || `tenant not found ${c.tenantId}` };
      });
  }, [tenants]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async () => {
    if (!createForm.email.trim()) return;
    setCreatingUser(true);
    try {
      await axiosInstance.post("/User", {
        EmailId: createForm.email,
        DisplayName: createForm.displayName,
        Role: createForm.role || undefined,
      });
      setMessage({ type: "success", text: "User created successfully." });
      setCreateDialogOpen(false);
      setCreateForm({ email: "", displayName: "", role: "" });
      fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error);
      setMessage({ type: "error", text: error.response?.data?.message || "Failed to create user." });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdateDisplayName = async () => {
    if (!editDisplayNameDialog || !editDisplayNameValue.trim()) return;
    setSavingDisplayName(true);
    try {
      await axiosInstance.put(`/User/${encodeURIComponent(editDisplayNameDialog.uid)}/update/displayname`, { displayName: editDisplayNameValue });
      setMessage({ type: "success", text: "Display name updated." });
      setEditDisplayNameDialog(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating display name:", error);
      setMessage({ type: "error", text: "Failed to update display name." });
    } finally {
      setSavingDisplayName(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;
    setDeletingUser(true);
    try {
      const claims = parseCustomClaims(deleteConfirm.customClaims);
      const tenantClaim = claims.find((c) => c.tenantId);
      const headers = tenantClaim ? { TenantId: tenantClaim.tenantId } : {};
      await axiosInstance.delete(`/User/${encodeURIComponent(deleteConfirm.uid)}`, { headers });
      setMessage({ type: "success", text: `User '${deleteConfirm.email}' deleted.` });
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setMessage({ type: "error", text: "Failed to delete user." });
    } finally {
      setDeletingUser(false);
    }
  };

  const handleOpenRoles = (user) => {
    setRolesDialog(user);
    const claims = parseCustomClaims(user.customClaims);
    setUserRoles(claims.map((c) => c.role));
    const linkedTenants = fetchUserTenants(user);
    setUserTenantMap((prev) => ({ ...prev, [user.uid]: linkedTenants }));
    setLoadingUserRoles(false);
  };

  const handleAssignRole = async () => {
    if (!rolesDialog || !assignRoleValue) return;
    setAssigningRole(true);
    try {
      if (assignTenantValue.length > 0) {
        await Promise.all(
          assignTenantValue.map((tenantId) =>
            axiosInstance.post("/User/assign", { userId: rolesDialog.uid, role: assignRoleValue, tenantId })
          )
        );
        const tenantNames = assignTenantValue.map((id) => {
          const t = tenants.find((tn) => tn.id === id);
          return t?.name || id;
        }).join(", ");
        setMessage({ type: "success", text: `Role '${getRoleLabel(assignRoleValue)}' assigned to: ${tenantNames}` });
      } else {
        await axiosInstance.post("/User/assign", { userId: rolesDialog.uid, role: assignRoleValue });
        setMessage({ type: "success", text: `Role '${getRoleLabel(assignRoleValue)}' assigned.` });
      }
      setUserRoles((prev) => [...prev, assignRoleValue]);
      setAssignRoleValue("");
      setAssignTenantValue([]);
      const updatedUsers = await axiosInstance.get("/Users/all");
      const allUsers = Array.isArray(updatedUsers.data) ? updatedUsers.data : [];
      setUsers(allUsers);
      const updatedUser = allUsers.find((u) => u.uid === rolesDialog.uid);
      if (updatedUser) setRolesDialog(updatedUser);
    } catch (error) {
      console.error("Error assigning role:", error);
      setMessage({ type: "error", text: "Failed to assign role." });
    } finally {
      setAssigningRole(false);
    }
  };

  const handleUnassignRole = async (role, tenantId) => {
    if (!rolesDialog) return;
    setUnassigningRole(true);
    try {
      const payload = { userId: rolesDialog.uid };
      if (tenantId) payload.tenantId = tenantId;
      await axiosInstance.post("/User/unassign", payload);
      const tenantName = tenantId ? (() => { const t = tenants.find((tn) => tn.id?.toLowerCase() === tenantId?.toLowerCase()); return t?.name || tenantId; })() : "";
      setMessage({ type: "success", text: `Role '${getRoleLabel(role)}'${tenantName ? ` from ${tenantName}` : ""} removed.` });
      const updatedUsers = await axiosInstance.get("/Users/all");
      const allUsers = Array.isArray(updatedUsers.data) ? updatedUsers.data : [];
      setUsers(allUsers);
      const updatedUser = allUsers.find((u) => u.uid === rolesDialog.uid);
      if (updatedUser) setRolesDialog(updatedUser);
    } catch (error) {
      console.error("Error unassigning role:", error);
      setMessage({ type: "error", text: "Failed to remove role." });
    } finally {
      setUnassigningRole(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (u.email || "").toLowerCase().includes(q) ||
      (u.displayName || "").toLowerCase().includes(q) ||
      (u.uid || "").toLowerCase().includes(q) ||
      parseCustomClaims(u.customClaims).some((c) => c.role.toLowerCase().includes(q) || (c.tenantId || "").toLowerCase().includes(q))
    );
  }).sort((a, b) => {
    if (!a.displayName && !b.displayName) return 0;
    if (!a.displayName) return 1;
    if (!b.displayName) return -1;
    return a.displayName.localeCompare(b.displayName);
  });

  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1, md: 2 },
        py: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          borderRadius: { xs: 4, md: 6 },
          background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))",
          boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.25, mb: 3.5 }}>
          <Box
            sx={{
              width: { xs: 56, md: 64 },
              height: { xs: 56, md: 64 },
              borderRadius: "18px",
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(249, 115, 22, 0.3)",
            }}
          >
            <PeopleOutlinedIcon sx={{ color: "#fff", fontSize: 32 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#202531" }}>
              Users Management
              {users.length > 0 && (
                <Typography component="span" variant="body2" sx={{ ml: 1.5, color: "#6b7280", fontWeight: 600 }}>
                  ({users.length})
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
              Manage system users, roles, and permissions.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              "&:hover": { background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)" },
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
            }}
          >
            Add User
          </Button>
        </Box>

        {message && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: message.type === "success" ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)", border: `1px solid ${message.type === "success" ? "rgba(34, 197, 94, 0.3)" : "rgba(239, 68, 68, 0.3)"}` }}>
            <Typography variant="body2" sx={{ color: message.type === "success" ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
              {message.text}
            </Typography>
            <IconButton size="small" onClick={() => setMessage(null)}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}

        <Box sx={{ mb: 2.5 }}>
          <TextField
            size="small"
            placeholder="Search by name, email, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: "#9ca3af", mr: 1, fontSize: 20 }} /> }}
            sx={{ width: { xs: "100%", md: 360 }, "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={36} sx={{ color: "#f97316" }} />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Typography variant="body2" sx={{ color: "#6b7280", textAlign: "center", py: 4 }}>
            {searchQuery ? "No users match your search." : "No users found."}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {filteredUsers.map((user) => (
              <Paper
                key={user.uid}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  background: "linear-gradient(180deg, rgba(255, 250, 245, 0.98), rgba(255,255,255,0.92))",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  "&:hover": { borderColor: "rgba(255, 132, 76, 0.25)", boxShadow: "0 4px 16px rgba(236, 123, 55, 0.08)" },
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
                    {(user.displayName || user.email || "?").charAt(0).toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#202531", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {user.displayName || "—"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                    {user.email || "—"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                    {(() => {
                      const claims = parseCustomClaims(user.customClaims);
                      return claims.map((c, idx) => {
                        if (c.tenantId) {
                          const t = tenants.find((tn) => tn.id?.toLowerCase() === c.tenantId?.toLowerCase());
                          const tenantName = t?.name || `tenant not found ${c.tenantId}`;
                          return (
                            <Chip
                              key={idx}
                              label={`${getRoleLabel(c.role)} - ${tenantName}`}
                              size="small"
                              title={c.tenantId}
                              sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, backgroundColor: "rgba(59, 130, 246, 0.08)", color: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.2)" }}
                            />
                          );
                        }
                        return (
                          <Chip
                            key={idx}
                            label={getRoleLabel(c.role)}
                            size="small"
                            sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, backgroundColor: "rgba(249, 115, 22, 0.08)", color: "#f97316", border: "1px solid rgba(249, 115, 22, 0.2)" }}
                          />
                        );
                      });
                    })()}
                    {user.disabled && (
                      <Chip label="Disabled" size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, backgroundColor: "rgba(239, 68, 68, 0.08)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.2)" }} />
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", alignItems: "flex-end", flexShrink: 0, mr: 1 }}>
                  {user.userMetaData?.lastSignInTimestamp && (
                    <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: "0.68rem" }}>
                      Last login: {new Date(user.userMetaData.lastSignInTimestamp).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: "#d1d5db", fontFamily: "monospace", fontSize: "0.65rem" }}>
                    {user.uid ? user.uid.substring(0, 12) + "..." : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Manage roles">
                    <IconButton size="small" onClick={() => handleOpenRoles(user)} sx={{ color: "#7c3aed", "&:hover": { backgroundColor: "rgba(124, 58, 237, 0.08)" } }}>
                      <SecurityOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit display name">
                    <IconButton size="small" onClick={() => { setEditDisplayNameDialog(user); setEditDisplayNameValue(user.displayName || ""); }} sx={{ color: "#f97316", "&:hover": { backgroundColor: "#fff7f1" } }}>
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <IconButton size="small" onClick={() => setDeleteConfirm(user)} sx={{ color: "#ef4444", "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" } }}>
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "20px", border: "1px solid rgba(255, 132, 76, 0.10)", background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))", boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>Create User</Typography>
          <IconButton onClick={() => setCreateDialogOpen(false)} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <TextField
              size="small"
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
            />
            <TextField
              size="small"
              label="Display Name"
              value={createForm.displayName}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, displayName: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
            />
            {AVAILABLE_ROLES.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ "&.Mui-focused": { color: "#f97316" } }}>Role</InputLabel>
                <Select
                  value={createForm.role}
                  label="Role"
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, role: e.target.value }))}
                  sx={{ borderRadius: "12px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" } }}
                >
                  <MenuItem value="">None</MenuItem>
                  {AVAILABLE_ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {getRoleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: "#6b7280" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={creatingUser || !createForm.email.trim()}
            sx={{ background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)", "&:hover": { background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            {creatingUser ? <CircularProgress size={16} color="inherit" /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Display Name Dialog */}
      <Dialog
        open={!!editDisplayNameDialog}
        onClose={() => setEditDisplayNameDialog(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "20px", border: "1px solid rgba(255, 132, 76, 0.10)", background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))", boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>Edit Display Name</Typography>
          <IconButton onClick={() => setEditDisplayNameDialog(null)} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="caption" sx={{ color: "#6b7280", mb: 1, display: "block" }}>
            {editDisplayNameDialog?.email}
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="Display Name"
            value={editDisplayNameValue}
            onChange={(e) => setEditDisplayNameValue(e.target.value)}
            sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditDisplayNameDialog(null)} sx={{ color: "#6b7280" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateDisplayName}
            disabled={savingDisplayName || !editDisplayNameValue.trim()}
            sx={{ background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)", "&:hover": { background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            {savingDisplayName ? <CircularProgress size={16} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{ sx: { borderRadius: "16px", border: "1px solid rgba(239, 68, 68, 0.15)", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#202531" }}>Delete User</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Are you sure you want to delete this user?
          </Typography>
          <Typography variant="body2" sx={{ color: "#202531", fontWeight: 600, mt: 1 }}>
            {deleteConfirm?.displayName || deleteConfirm?.email}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mt: 0.5 }}>
            {deleteConfirm?.email}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ color: "#6b7280" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDeleteUser}
            disabled={deletingUser}
            sx={{ backgroundColor: "#ef4444", "&:hover": { backgroundColor: "#dc2626" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
          >
            {deletingUser ? <CircularProgress size={16} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Roles Management Dialog */}
      <Dialog
        open={!!rolesDialog}
        onClose={() => { setRolesDialog(null); setUserRoles([]); setAssignRoleValue(""); setAssignTenantValue([]); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "20px", border: "1px solid rgba(255, 132, 76, 0.10)", background: "linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))", boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#202531" }}>Manage Roles & Tenant</Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>{rolesDialog?.displayName || rolesDialog?.email}</Typography>
            {rolesDialog && (userTenantMap[rolesDialog.uid] || []).length > 0 && (
              <Typography variant="caption" sx={{ color: "#3b82f6", display: "block" }}>
                Tenants: {(userTenantMap[rolesDialog.uid] || []).map((t) => t.tenantName).join(", ")}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => { setRolesDialog(null); setUserRoles([]); setAssignRoleValue(""); setAssignTenantValue([]); }} sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#fff7f1", color: "#f97316" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {loadingUserRoles ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: "#f97316" }} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#202531" }}>Current Roles</Typography>
                {unassigningRole && <CircularProgress size={16} sx={{ color: "#f97316" }} />}
              </Box>
              {rolesDialog && parseCustomClaims(rolesDialog.customClaims).length === 0 ? (
                <Typography variant="caption" sx={{ color: "#6b7280" }}>No roles assigned.</Typography>
              ) : (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {rolesDialog && parseCustomClaims(rolesDialog.customClaims).map((claim, idx) => {
                    let label = getRoleLabel(claim.role);
                    if (claim.tenantId) {
                      const t = tenants.find((tn) => tn.id?.toLowerCase() === claim.tenantId?.toLowerCase());
                      label += ` - ${t?.name || claim.tenantId}`;
                    }
                    return (
                      <Chip
                        key={idx}
                        label={label}
                        disabled={unassigningRole}
                        onDelete={() => handleUnassignRole(claim.role, claim.tenantId)}
                        sx={{ fontWeight: 600, backgroundColor: claim.tenantId ? "rgba(59, 130, 246, 0.08)" : "rgba(249, 115, 22, 0.08)", color: claim.tenantId ? "#3b82f6" : "#f97316", border: `1px solid ${claim.tenantId ? "rgba(59, 130, 246, 0.2)" : "rgba(249, 115, 22, 0.2)"}`, "& .MuiChip-deleteIcon": { color: "#ef4444", "&:hover": { color: "#dc2626" } } }}
                      />
                    );
                  })}
                </Box>
              )}
              <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid rgba(255, 132, 76, 0.10)" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#202531", mb: 1.5 }}>Assign Role & Tenant</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel sx={{ "&.Mui-focused": { color: "#f97316" } }}>Role</InputLabel>
                      <Select
                        value={assignRoleValue}
                        label="Role"
                        displayEmpty
                        onChange={(e) => setAssignRoleValue(e.target.value)}
                        sx={{ borderRadius: "12px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" } }}
                      >
                        {AVAILABLE_ROLES.map((role) => (
                          <MenuItem key={role} value={role}>{getRoleLabel(role)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Autocomplete
                      multiple
                      size="small"
                      sx={{ flex: 1 }}
                      options={[...tenants].sort((a, b) => (a.name || "").localeCompare(b.name || ""))}
                      getOptionLabel={(option) => option.name || option.id || ""}
                      value={tenants.filter((t) => assignTenantValue.includes(t.id))}
                      onChange={(_, newValue) => setAssignTenantValue(newValue.map((t) => t.id))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tenant(s)"
                          placeholder="Search tenants..."
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", "& fieldset": { borderColor: "rgba(255, 132, 76, 0.20)" }, "&:hover fieldset": { borderColor: "#f97316" }, "&.Mui-focused fieldset": { borderColor: "#f97316" } } }}
                        />
                      )}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleAssignRole}
                    disabled={assigningRole || !assignRoleValue}
                    sx={{ background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)", "&:hover": { background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)" }, borderRadius: "10px", textTransform: "none", fontWeight: 700, alignSelf: "flex-start", px: 3 }}
                  >
                    {assigningRole ? <CircularProgress size={16} color="inherit" /> : "Assign"}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UsersManagementTab;
